import {Col, Divider, Row} from "antd";
import {useTranslation} from "react-i18next";
import FieldBuilder from "../../form-components/field-builder";
import {languages} from "../../../utils/helpers/constants";
import {MediumFor} from "../../../models/medium/enum";
import DiscountCodeContext from "../../../context/discount-code/context";
import {useContext, useEffect, useState} from "react";
import EndPoints from "../../../services/end-points";
import {IMedium} from "../../../models/medium/response";
import ImageFieldBlock from "../../general/image-field-block";
import {errorNotification, successNotification} from "../../../utils/helpers/notification";
import {appendImageProcessingFields, getImageProcessingMode, hasPendingImageUpload, resolveUploadFile} from "../../../utils/helpers/media-upload";

export const AdditionalSettingsSection = ({setValue}) => {
    const {t} = useTranslation();
    const {details} = useContext(DiscountCodeContext);
    const [newImages, setNewImages] = useState<Record<string, string | null>>({});
    const [newImageFiles, setNewImageFiles] = useState<Record<string, File | undefined>>({});
    const [originalImageFiles, setOriginalImageFiles] = useState<Record<string, File | undefined>>({});
    const [imageChanged, setImageChanged] = useState<Record<string, boolean>>({});
    const [imageLoading, setImageLoading] = useState<Record<string, boolean>>({});
    const [withoutBackgrounds, setWithoutBackgrounds] = useState<Record<string, boolean>>({});
    const [preserveOriginals, setPreserveOriginals] = useState<Record<string, boolean>>({});
    const handleImageChange = (fieldName: string) => (response: IMedium | null) => {
        setNewImages((prev) => ({...prev, [fieldName]: response?.url ?? null}));
        setNewImageFiles((prev) => ({...prev, [fieldName]: response?.file}));
        setOriginalImageFiles((prev) => ({...prev, [fieldName]: response?.originalFile}));
        setImageChanged((prev) => ({...prev, [fieldName]: Boolean(response)}));
    };

    useEffect(() => {
        setValue("__pendingImageUploads", hasPendingImageUpload(imageChanged, newImages), {shouldDirty: false});
    }, [imageChanged, newImages, setValue]);

    const handleUploadNewImage = async (fieldName: string) => {
        setImageLoading(prev => ({...prev, [fieldName]: true}));
        const newImageUrl = newImages[fieldName];
        if (!newImageUrl) {
            setImageLoading(prev => ({...prev, [fieldName]: false}));
            return;
        }

        try {
            const isNotificationImage = fieldName === "notifyImageId";
            const file = isNotificationImage
                ? await resolveUploadFile({
                    imageUrl: newImageUrl,
                    processedFile: newImageFiles[fieldName],
                    fallbackName: fieldName,
                })
                : await resolveUploadFile({
                    imageUrl: newImageUrl,
                    processedFile: newImageFiles[fieldName],
                    originalFile: originalImageFiles[fieldName],
                    preserveOriginal: preserveOriginals[fieldName] ?? false,
                    fallbackName: fieldName,
                });

            const formData = new FormData();
            formData.append("medium", file);
            formData.append("type", "1");
            formData.append("for", MediumFor.UserProfile);

            if (isNotificationImage) {
                formData.append("is_notification", "1");
            } else {
                const preserveOriginal = preserveOriginals[fieldName] ?? false;
                appendImageProcessingFields(
                    formData,
                    getImageProcessingMode(withoutBackgrounds[fieldName] ?? false, preserveOriginal),
                );
            }

            const uploadRes = await EndPoints.media.uploadMedia(formData);
            setValue(fieldName, uploadRes.id, {shouldDirty: true});
            setImageChanged((prev) => ({...prev, [fieldName]: false}));
            setNewImages((prev) => ({...prev, [fieldName]: uploadRes?.url ?? newImageUrl}));
            setNewImageFiles((prev) => ({...prev, [fieldName]: undefined}));
            setOriginalImageFiles((prev) => ({...prev, [fieldName]: undefined}));
            setPreserveOriginals((prev) => ({...prev, [fieldName]: false}));
            successNotification(t(`image_uploaded_successfully`))
        } catch (err) {
            console.error("Upload failed", err);
        } finally {
            setImageLoading(prev => ({...prev, [fieldName]: false}));

        }
    };

    const handleDeleteImage = async (fieldName: string) => {
        try {
            setImageLoading(prev => ({...prev, [fieldName]: true}));
            setValue(fieldName, null);
        } finally {
            setImageLoading(prev => ({...prev, [fieldName]: false}));
        }
    };

    const handleRemoveNewImage = (fieldName: string, restoreCurrentImage = true) => {
        const currentImage = restoreCurrentImage
            ? fieldName === "notifyImageId" ? details?.notifyImage : details?.image
            : undefined;

        setValue(fieldName, currentImage?.id ?? undefined, {shouldDirty: true});
        setImageChanged((prev) => ({...prev, [fieldName]: false}));
        setNewImages((prev) => ({...prev, [fieldName]: null}));
        setNewImageFiles((prev) => ({...prev, [fieldName]: undefined}));
        setOriginalImageFiles((prev) => ({...prev, [fieldName]: undefined}));
        setPreserveOriginals((prev) => ({...prev, [fieldName]: false}));
        setWithoutBackgrounds((prev) => ({
            ...prev,
            [fieldName]: Boolean(currentImage?.without_background),
        }));
    };


    return (
        <>
            <Divider orientation="center">{t("additional_settings")}</Divider>
            <Row gutter={[16, 8]}>
                <Col span={24}>
                    <FieldBuilder name="message" label={t("message")} input={{type: "text-area"}}/>
                </Col>
                {languages?.map((lang, index) => (
                    <Col span={12} key={index}>
                        <FieldBuilder
                            name={`note[${lang?.code}]`}
                            label={`${t("note")} (${lang?.name})`}
                            input={{type: "text-area"}}
                            rules={{required: false}}
                        />
                    </Col>
                ))}
                <Col span={24}>
                    <ImageFieldBlock
                        fieldName="imageId"
                        currentId={details?.image?.id}
                        fieldValue={withoutBackgrounds["imageId"]}
                        currentImage={details?.image}
                        newImageUrl={newImages["imageId"] ?? null}
                        isChanged={imageChanged["imageId"]}
                        onUpload={() => handleUploadNewImage("imageId")}
                        onImageChange={handleImageChange("imageId")}
                        onRemoveNewImage={(restoreCurrentImage) => handleRemoveNewImage("imageId", restoreCurrentImage)}
                        setWithoutBackgrounds={(val) => setWithoutBackgrounds(prev => ({...prev, imageId: val}))}
                        preserveOriginal={preserveOriginals["imageId"]}
                        setPreserveOriginal={(val) => setPreserveOriginals(prev => ({...prev, imageId: val}))}
                        onDeleteImage={() => handleDeleteImage("imageId")}
                        isImageLoading={imageLoading["imageId"]}
                        value={withoutBackgrounds["imageId"]}
                        force={true}
                        notRequired={true}
                    />
                </Col>
                <Col span={24}>
                    <ImageFieldBlock
                        title="notify_image"
                        fieldName="notifyImageId"
                        currentId={details?.notifyImage?.id}
                        currentImage={details?.notifyImage}
                        newImageUrl={newImages["notifyImageId"] ?? null}
                        isChanged={imageChanged["notifyImageId"]}
                        onUpload={() => handleUploadNewImage("notifyImageId")}
                        onImageChange={handleImageChange("notifyImageId")}
                        onRemoveNewImage={(restoreCurrentImage) => handleRemoveNewImage("notifyImageId", restoreCurrentImage)}
                        onDeleteImage={() => handleDeleteImage("notifyImageId")}
                        isImageLoading={imageLoading["notifyImageId"]}
                        force={true}
                        notRequired={true}
                        isNotification={true}
                    />
                </Col>
            </Row>
        </>
    );
};
